import type { FormField } from '@/models/form';
import type { ScannedField } from '@/models/scannedField';

/**
 * What a saved entry draws from its scanned fields as spdata-api read them (ENG-10012, ADR 0016): the "Scanned · 14:05"
 * chip on each field, and the "Values from" notice over the fields. The server decided which fields still hold a scan's
 * answer when the entry opened; the client only takes back the chip of a field changed since.
 */

/**
 * Each field that draws a chip, by identifier, with the row it draws from. A row names its field by `fieldId`, the
 * identifier its answer is stored under, and by its variable when that id names no field on the form. A field changed
 * since the entry opened draws none, since its answer may no longer be the scan's.
 */
export const savedScanChips = (
  rows: readonly ScannedField[],
  fields: readonly FormField[],
  changed: ReadonlySet<string>
): Map<string, ScannedField> => {
  const chips = new Map<string, ScannedField>();
  for (const row of rows) {
    const field = fields.find(item => item.identifier === row.fieldId) ??
      fields.find(item => !!item.variableName && item.variableName === row.variable);
    if (field && !changed.has(field.identifier) && !chips.has(field.identifier)) {
      chips.set(field.identifier, row);
    }
  }
  return chips;
};

/**
 * The scans the notice links to, oldest first: one row per scan, its first field's, which the scan detail highlights
 * when the link opens it. A deleted scan is left out, because there is nothing left to open.
 */
export const noticeScans = (chips: ReadonlyMap<string, ScannedField>): ScannedField[] => {
  const byScan = new Map<string, ScannedField>();
  for (const row of chips.values()) {
    if (!row.scan.archived && !byScan.has(row.scan.id)) {
      byScan.set(row.scan.id, row);
    }
  }
  return [...byScan.values()].sort((a, b) => a.scan.recordedAt.getTime() - b.scan.recordedAt.getTime());
};
